import { CardWrapper } from '@app/features/analytics/components/card/card';
import { BarChart } from '@app/features/analytics/components/charts/bar-chart';
import {
  BarChartData,
  DotChartData,
} from '@app/features/analytics/components/charts/helpers';
import { DotChart } from '@app/features/analytics/components/charts/line-dot-chart';
import { ErrorScreen } from '@app/features/analytics/components/error-states/error-screen';
import { NoFilterResults } from '@app/features/analytics/components/error-states/no-filter-results';
import { NoResults } from '@app/features/analytics/components/error-states/no-results';
import { FLAG_ENABLE_CH_DASHBOARD_ANALYTICS } from '@app/features/analytics/constants';
import { useAnalytics } from '@app/features/analytics/hooks/use-analytics';
import { useFetch } from '@app/features/analytics/hooks/use-fetch';
import { useSpaceContext } from '@app/features/analytics/hooks/use-space-context';
import { AverageEngagementScore } from '@app/features/analytics/icons/average-engagement-score';
import { AverageEngagementScoreActive } from '@app/features/analytics/icons/average-engagement-score-active';
import { AverageTurnout } from '@app/features/analytics/icons/average-turnout';
import { AverageTurnoutActive } from '@app/features/analytics/icons/average-turnout-active';
import { TotalHubViews } from '@app/features/analytics/icons/total-hub-views';
import { TotalHubViewsActive } from '@app/features/analytics/icons/total-hub-views-active';
import { TotalRegistrations } from '@app/features/analytics/icons/total-registrations';
import { TotalRegistrationsActive } from '@app/features/analytics/icons/total-registrations-active';
import { useAnalyticsContext } from '@app/features/analytics/providers/analytics-provider';
import {
  fetchTotalEngagementOverTime,
  fetchTotalPageViewsOverTime,
  fetchTotalRegistrantsOverTime,
  fetchTotals,
  fetchTotalTurnoutOverTime,
} from '@app/features/analytics/services/endpoints';
import { OutlineOnFocus } from '@app/features/analytics/styles';
import { AllowedEvents } from '@app/features/analytics/types';
import { useFlags } from '@app/hooks/use-flags';
import { Tab } from '@headlessui/react';
import { useReadDashboardSharedState } from '@hopin-team/dashboard-shared-react';
import { useTranslation } from '@hopin-team/provider-translation';
import cx from 'classnames';
import React, { useEffect, useState } from 'react';

import { LoaderSkeletonChart, LoaderSkeletonTab } from './loader';

const ChartTabs = [
  {
    title: 'analytics.total-registrations',
    icon: <TotalRegistrations />,
    activeIcon: <TotalRegistrationsActive />,
  },
  {
    title: 'analytics.average-turnout',
    icon: <AverageTurnout />,
    activeIcon: <AverageTurnoutActive />,
  },
  {
    title: 'analytics.average-engagement-score',
    icon: <AverageEngagementScore />,
    activeIcon: <AverageEngagementScoreActive />,
  },
];

const newChartTabs = [
  {
    title: 'analytics.total-hub-visits',
    icon: <TotalHubViews />,
    activeIcon: <TotalHubViewsActive />,
  },
];

const GetChartTab = ({
  isError,
  data,
  isInitialLoad,
  selectedIndex,
}: {
  isError: boolean;
  data: BarChartData | DotChartData;
  isInitialLoad: boolean;
  selectedIndex: number;
}) => {
  const { t } = useTranslation();
  if (isError) {
    return <ErrorScreen showButton={false} />;
  }
  if (data.length === 0) {
    return isInitialLoad ? <NoResults /> : <NoFilterResults />;
  }
  return selectedIndex === 0 || selectedIndex === 3 ? (
    <BarChart
      chartData={data as BarChartData}
      tooltipTitle={
        selectedIndex === 0
          ? t('analytics.total-registrations')
          : t('analytics.total-hub-visits')
      }
    />
  ) : (
    <DotChart
      chartData={data as DotChartData}
      tooltipTitle={
        selectedIndex == 1
          ? t('analytics.turnout')
          : t('analytics.engagement-score')
      }
    />
  );
};

const getQueryData = (selectedIndex: number) => {
  if (selectedIndex === 0) {
    return {
      queryName: 'totalRegistrantsOverTime',
      endPoint: fetchTotalRegistrantsOverTime,
    };
  } else if (selectedIndex === 1) {
    return {
      queryName: 'totalTurnoutOverTime',
      endPoint: fetchTotalTurnoutOverTime,
    };
  } else if (selectedIndex === 2) {
    return {
      queryName: 'totalEngagementOverTime',
      endPoint: fetchTotalEngagementOverTime,
    };
  } else {
    return {
      queryName: 'totalPageViewsOverTime',
      endPoint: fetchTotalPageViewsOverTime,
    };
  }
};
const chartAnalyticsEvents: AllowedEvents[] = [
  'ola_overview_total_registrations_click',
  'ola_overview_average_turnout_click',
  'ola_overview_average_engagement_score_click',
  'ola_overview_total_hub_visits_click',
];
export const Charts = () => {
  const { t } = useTranslation();
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const { state, dispatch } = useAnalyticsContext();
  const {
    chartSelectedIndex,
    eventTypes,
    dateRange,
    startDate,
    endDate,
    selectedEvents,
  } = state;
  const selectedIndex = chartSelectedIndex;
  const { track } = useAnalytics();
  const setSelectedIndex = (index: number) => {
    dispatch({
      type: 'analytics chart tab updated',
      payload: { index: index },
    });
    const analyticsEvent = chartAnalyticsEvents[index];
    if (analyticsEvent) {
      track(analyticsEvent, {
        tabName: ChartTabs[index]?.title,
      });
    }
  };

  const flags = useFlags();
  const orgId: string = useReadDashboardSharedState('org_id') || '';
  const { spaceExternalId } = useSpaceContext();

  const enableCHDashboardAnalytics =
    flags?.[FLAG_ENABLE_CH_DASHBOARD_ANALYTICS];

  const {
    data: totalData,
    isLoading: totalIsLoading,
    isError: totalIsError,
  } = useFetch(
    {
      eventTypes,
      dateRange,
      startDate,
      endDate,
      selectedEvents,
      spaceExternalId,
    },
    `totals-${orgId}`,
    fetchTotals,
  );

  const config = {
    eventTypes,
    dateRange,
    startTime: startDate || dateRange,
    endTime: endDate || new Date().toISOString().split('T')[0],
    ...(selectedIndex !== 3 && { selectedEvents }),
    spaceExternalId,
  };

  const { queryName, endPoint } = getQueryData(selectedIndex);
  const { data, isLoading, isError } = useFetch(
    { ...config },
    `${queryName}-${orgId}`,
    endPoint,
  );

  const chartData: BarChartData = Array.isArray(data) ? data : [];

  const eventNumber = !totalIsError ? totalData?.eventsNumber || 0 : 0;
  const pagesCount = !totalIsError ? totalData?.pagesCount || 0 : 0;

  useEffect(() => {
    if (!isLoading && !totalIsLoading) {
      setIsInitialLoad(false);
    }
  }, [isLoading, totalIsLoading]);

  const trimChartData = (dateRange: string, chartData: BarChartData) => {
    if (dateRange !== 'all-time') {
      return chartData;
    }
    const data = [...chartData];
    const firstAvailableDataIndex = data.findIndex(item => item[1] !== 0);
    if (firstAvailableDataIndex !== -1) {
      return data.slice(firstAvailableDataIndex);
    }
    return data;
  };

  const isHubVisitsChart = selectedIndex === 3;

  const totalCount = isHubVisitsChart ? pagesCount : eventNumber;
  const chartDataTypeTranslationKey = isHubVisitsChart
    ? totalCount === 1
      ? t('analytics.hub')
      : t('analytics.hubs')
    : totalCount === 1
    ? t('analytics.event')
    : t('analytics.events');

  return (
    <CardWrapper>
      <>
        <div
          className={cx('grid gap-x-6 min-h-[21.25rem]', {
            'grid-cols-[1fr_20%]': !enableCHDashboardAnalytics,
            'grid-cols-[1fr_40%]': enableCHDashboardAnalytics,
          })}
        >
          <Tab.Group selectedIndex={selectedIndex} onChange={setSelectedIndex}>
            {isLoading ? (
              <LoaderSkeletonChart />
            ) : (
              <div className="flex flex-col">
                <div className="text-black text-sm font-medium font-inter mb-6">
                  {totalCount} {t(chartDataTypeTranslationKey)}
                </div>
                <Tab.Panels className="flex-1">
                  <Tab.Panel className="h-full w-full">
                    <GetChartTab
                      isError={isError}
                      data={trimChartData(dateRange, chartData)}
                      isInitialLoad={isInitialLoad}
                      selectedIndex={selectedIndex}
                    />
                  </Tab.Panel>
                  <Tab.Panel className="h-full w-full">
                    <GetChartTab
                      isError={isError}
                      data={chartData || []}
                      isInitialLoad={isInitialLoad}
                      selectedIndex={selectedIndex}
                    />
                  </Tab.Panel>
                  <Tab.Panel className="h-full w-full">
                    <GetChartTab
                      isError={isError}
                      data={chartData || []}
                      isInitialLoad={isInitialLoad}
                      selectedIndex={selectedIndex}
                    />
                  </Tab.Panel>
                  <Tab.Panel className="h-full w-full">
                    <GetChartTab
                      isError={isError}
                      data={trimChartData(dateRange, chartData)}
                      isInitialLoad={isInitialLoad}
                      selectedIndex={selectedIndex}
                    />
                  </Tab.Panel>
                </Tab.Panels>
              </div>
            )}
            {totalIsLoading ? (
              <LoaderSkeletonTab />
            ) : (
              <Tab.List
                className={cx('gap-4', {
                  'flex flex-col': !enableCHDashboardAnalytics,
                  'grid grid-rows-2 grid-cols-2': enableCHDashboardAnalytics,
                })}
              >
                {[
                  ...ChartTabs,
                  ...(enableCHDashboardAnalytics ? newChartTabs : []),
                ].map((data, id) => (
                  <Tab
                    key={id}
                    className={({ selected }) =>
                      `p-5 rounded-lg outline-none border flex flex-col justify-between min-h-28 ${
                        selected
                          ? 'bg-active border-blue-500'
                          : 'bg-transparent border-gray-300'
                      }` + OutlineOnFocus
                    }
                  >
                    <div className="flex gap-2 items-center">
                      <div>
                        {selectedIndex == id ? data.activeIcon : data.icon}
                      </div>
                      <div className="text-black text-sm font-medium font-inter">
                        {t(data.title)}
                      </div>
                    </div>
                    <div className="flex items-center">
                      <div className="flex-1 text-black text-[17px] font-medium font-inter text-left">
                        {id == 0
                          ? !totalIsError
                            ? totalData.registrantsNumber || 0
                            : 0
                          : id == 1
                          ? !totalIsError
                            ? (totalData.turnout || 0) + '%'
                            : '0%'
                          : id === 2
                          ? !totalIsError
                            ? (totalData.engagementScore || 0) + '%'
                            : '0%'
                          : !totalIsError
                          ? totalData.pageViews || 0
                          : 0}
                      </div>
                    </div>
                  </Tab>
                ))}
              </Tab.List>
            )}
          </Tab.Group>
        </div>
      </>
    </CardWrapper>
  );
};
